import { useRef, useState } from "react";
import { useUI, type Filter, type View } from "../store/ui";
import { useRH } from "../store/rh";
import { snapshotElement } from "../lib/snapshot";
import { Settings } from "./Settings";
import { useSettings } from "../store/settings";

const TABS: { id: View; label: string }[] = [
  { id: "about",    label: "Sobre" },
  { id: "clients",  label: "Clientes" },
  { id: "rh",       label: "RH" },
  { id: "office",   label: "Escritório" },
  { id: "demands",  label: "Demandas" },
  { id: "funnel",   label: "🧭 Funil" },
  { id: "ops",      label: "🎬 Operações" },
  { id: "gallery",  label: "Galeria" },
  { id: "library",  label: "📚 Biblioteca" },
  { id: "social",   label: "Social" },
  { id: "meeting",  label: "Sala" },
  { id: "meetings", label: "Arquivo" },
  { id: "briefing", label: "Boletim" },
  { id: "log",      label: "Histórico" }
];

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all",       label: "Todos" },
  { id: "hired",     label: "Contratados" },
  { id: "available", label: "Disponíveis" },
  { id: "fired",     label: "Demitidos" }
];

export function Toolbar() {
  const view       = useUI(s => s.view);
  const setView    = useUI(s => s.setView);
  const filter     = useUI(s => s.filter);
  const setFilter  = useUI(s => s.setFilter);
  const search     = useUI(s => s.search);
  const setSearch  = useUI(s => s.setSearch);
  const meetCount  = useUI(s => s.meetingPicks.length);
  const hasKey     = useSettings(s => s.anthropicKey.length > 0);

  const [showSettings, setShowSettings] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const exportJson = () => {
    const data = JSON.stringify({ rh: useRH.getState(), ui: useUI.getState() }, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `agencia-vpc-rh-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importJson = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const obj = JSON.parse(String(reader.result));
        if (!confirm("Importar substituirá seu RH atual. Continuar?")) return;
        if (obj?.rh?.employees) {
          useRH.setState({
            employees: obj.rh.employees,
            events: obj.rh.events ?? []
          });
        }
        if (obj?.ui?.savedMeetings) {
          useUI.setState({ savedMeetings: obj.ui.savedMeetings });
        }
        alert("Importado com sucesso.");
      } catch (e) {
        alert("JSON inválido: " + (e as Error).message);
      }
    };
    reader.readAsText(file);
  };

  const snapshot = async () => {
    const el = document.getElementById("app-root");
    if (!el) return;
    const stamp = new Date().toISOString().slice(0, 10);
    await snapshotElement(el, `agencia-vpc-rh-${stamp}.png`);
  };

  return (
    <>
      <div data-snapshot-ignore="true" className="sticky top-0 z-30 bg-bone border-b-2 border-ink">
        <div className="px-8 py-3 flex flex-wrap items-center gap-3">
          <div className="flex border border-ink">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => setView(t.id)}
                className={`px-3 py-1.5 tag border-r last:border-r-0 border-ink
                  ${view === t.id ? "bg-ink text-bone" : "bg-bone text-ink hover:bg-ink/10"}`}
              >
                {t.label}
                {t.id === "meeting" && meetCount > 0 && (
                  <span className="ml-1.5 px-1 bg-red text-bone">{meetCount}</span>
                )}
              </button>
            ))}
          </div>

          {view === "rh" && (
            <div className="flex border border-ink">
              {FILTERS.map(f => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-3 py-1.5 tag border-r last:border-r-0 border-ink
                    ${filter === f.id ? "bg-gold text-ink" : "bg-bone text-ink hover:bg-gold/30"}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 border border-ink px-2 py-1 bg-bone ml-auto">
            <span className="tag text-graydk">buscar</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="nome / palavra-chave"
              className="bg-transparent outline-none font-mono text-[12px] w-44"
            />
            {search && <button onClick={() => setSearch("")} className="tag text-red">×</button>}
          </div>

          <button onClick={snapshot}   className="btn btn-fire">📸 png</button>
          <button onClick={exportJson} className="btn btn-fire">↓ json</button>
          <button onClick={() => fileInputRef.current?.click()} className="btn btn-fire">↑ json</button>
          <button
            onClick={() => setShowSettings(true)}
            className={`btn ${hasKey ? "btn-fire" : "bg-gold text-ink border-gold"}`}
            title="Configurações / Chave Anthropic"
          >
            ⚙️ {hasKey ? "" : "configurar IA"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) importJson(f);
              if (e.target) e.target.value = "";
            }}
          />
        </div>
      </div>
      {showSettings && <Settings onClose={() => setShowSettings(false)} />}
    </>
  );
}
